'use client';

import { useState, useEffect } from 'react';
import { Mail, Lock, User, Key, Loader2, CheckCircle, ShieldQuestion, LogIn, UserPlus, Check } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { AgButton, AgInput } from '@/components/ui/agartha';
import './auth.css';
import { useAuth } from '@/context/AuthContext';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/lib/navigation';

function InputField({ icon, error, ...props }) {
    return (
        <div className="auth-field">
            <AgInput icon={icon} error={error} {...props} />
            {error && <p className="auth-field__error">{error}</p>}
        </div>
    );
}

function CheckRow({ id, name, checked, onChange, invalid, required, children }) {
    return (
        <label htmlFor={id} className={`auth-check ${invalid ? 'is-invalid' : ''}`}>
            <input type="checkbox" id={id} name={name} checked={checked} onChange={onChange} required={required} />
            <span className="auth-check__box" aria-hidden><Check /></span>
            <span className="auth-check__text">{children}</span>
        </label>
    );
}

export default function AuthModal({ isOpen, onClose, initialView = 'LOGIN' }) {
    const t = useTranslations('Auth');
    const locale = useLocale();
    const { login, register, verify, forgotPassword } = useAuth();
    const [view, setView] = useState(initialView); // LOGIN, REGISTER, VERIFY, FORGOT_PASSWORD
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        code: '',
        rodoAccepted: false,
        subscribeNewsletter: false
    });

    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    // Sync view when initialView changes or modal opens
    useEffect(() => {
        if (isOpen) {
            setView(initialView);
            setError('');
            setFieldErrors({});
            setSuccessMsg('');
        }
    }, [isOpen, initialView]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? checked : value 
        });
        setError('');
        setFieldErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setFieldErrors({});
        setSuccessMsg('');

        try {
            if (view === 'LOGIN') {
                await login({ email: formData.email, password: formData.password });
                onClose();
            } else if (view === 'REGISTER') {
                if (!formData.rodoAccepted) {
                    setError(t('error_rodo'));
                    setLoading(false);
                    return;
                }
                // Migracja guest progress z localStorage (XP/level zebrane jako gość)
                const guestProgress = typeof window !== 'undefined'
                    ? {
                        level: parseInt(localStorage.getItem('guest_accountLevel') || '1', 10) || 1,
                        xp: parseInt(localStorage.getItem('guest_accountXp') || '0', 10) || 0,
                        totalXp: parseInt(localStorage.getItem('guest_accountTotalXp') || '0', 10) || 0
                    }
                    : null;
                await register({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password,
                    rodoAccepted: formData.rodoAccepted,
                    subscribeNewsletter: formData.subscribeNewsletter,
                    guestProgress
                });
                // Po udanej rejestracji czyścimy guest progress – użytkownik dostał XP na koncie
                if (typeof window !== 'undefined') {
                    try {
                        localStorage.removeItem('guest_accountLevel');
                        localStorage.removeItem('guest_accountXp');
                        localStorage.removeItem('guest_accountTotalXp');
                    } catch {}
                }
                setSuccessMsg(t('success_register'));
                setView('VERIFY');
            } else if (view === 'VERIFY') {
                await verify({ email: formData.email, code: formData.code });
                onClose();
            } else if (view === 'FORGOT_PASSWORD') {
                await forgotPassword({ email: formData.email, locale });
                setSuccessMsg(t('forgot_password_success'));
            }
        } catch (err) {
            const backendMsg = err.response?.data?.message;
            const backendErrors = err.response?.data?.errors;
            
            setError(backendMsg || t('error_generic'));
            if (backendErrors) {
                setFieldErrors(backendErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const VIEWS = {
        LOGIN: { title: t('login_title'), sub: t('login_sub'), icon: LogIn },
        REGISTER: { title: t('register_title'), sub: t('register_sub'), icon: UserPlus },
        VERIFY: { title: t('verify_title'), sub: t('verify_sub'), icon: Key },
        FORGOT_PASSWORD: { title: t('forgot_password_title'), sub: t('forgot_sub'), icon: ShieldQuestion },
    };
    const v = VIEWS[view] || VIEWS.LOGIN;
    const go = (next) => { setError(''); setSuccessMsg(''); setView(next); };
    const formError = error && !Object.keys(fieldErrors).length ? <p className="auth-error">{error}</p> : null;
    const submit = (label, opts = {}) => (
        <AgButton type="submit" variant="primary" disabled={loading || opts.disabled} className="auth-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
        </AgButton>
    );
    const success = successMsg ? <div className="auth-success"><CheckCircle />{successMsg}</div> : null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={v.title} subtitle={v.sub} icon={v.icon} size="sm" zIndex={10050}>
            {view === 'LOGIN' && (
                <form onSubmit={handleSubmit} className="auth-form">
                    <InputField icon={Mail} type="email" name="email" placeholder={t('email_placeholder')} value={formData.email} onChange={handleChange} error={fieldErrors.email} autoComplete="email" required />
                    <InputField icon={Lock} type="password" name="password" placeholder={t('password_placeholder')} value={formData.password} onChange={handleChange} error={fieldErrors.password} autoComplete="current-password" required />
                    <button type="button" className="auth-link auth-link--right" onClick={() => go('FORGOT_PASSWORD')}>{t('forgot_password')}</button>
                    {formError}
                    {submit(t('login'), { arrow: true })}
                    <div className="auth-sep"><span>{t('or')}</span></div>
                    <button type="button" className="auth-alt" onClick={() => go('REGISTER')}><UserPlus />{t('no_account')}</button>
                </form>
            )}

            {view === 'REGISTER' && (
                <form onSubmit={handleSubmit} className="auth-form">
                    <InputField icon={User} type="text" name="username" placeholder={t('username_placeholder')} value={formData.username} onChange={handleChange} error={fieldErrors.username} autoComplete="nickname" required />
                    <InputField icon={Mail} type="email" name="email" placeholder={t('email_placeholder')} value={formData.email} onChange={handleChange} error={fieldErrors.email} autoComplete="email" required />
                    <InputField icon={Lock} type="password" name="password" placeholder={t('password_placeholder')} value={formData.password} onChange={handleChange} error={fieldErrors.password} autoComplete="new-password" required />
                    <CheckRow id="rodoAccepted" name="rodoAccepted" checked={formData.rodoAccepted} onChange={handleChange} invalid={!!error && !formData.rodoAccepted} required>
                        {t.rich('rodo_accept', {
                            rodo: () => <Link href="/rodo" target="_blank" className="auth-strong auth-strong--link">{t('rodo_label')}</Link>
                        })}
                    </CheckRow>
                    <CheckRow id="subscribeNewsletter" name="subscribeNewsletter" checked={formData.subscribeNewsletter} onChange={handleChange}>
                        {t.rich('newsletter_accept', {
                            newsletter: () => <span className="auth-strong">{t('newsletter_label')}</span>
                        })}
                    </CheckRow>
                    {formError}
                    {submit(t('register'))}
                    <div className="auth-links">
                        <button type="button" className="auth-link" onClick={() => go('VERIFY')}>{t('have_code')}</button>
                        <button type="button" className="auth-link" onClick={() => go('LOGIN')}>{t('have_account')}</button>
                    </div>
                </form>
            )}

            {view === 'VERIFY' && (
                <form onSubmit={handleSubmit} className="auth-form">
                    <p className="auth-desc">
                        {t.rich('verify_desc', { email: () => <strong className="auth-strong">{formData.email}</strong> })}
                        <br /><small>{t('verify_dev_hint')}</small>
                    </p>
                    {success}
                    <InputField
                        icon={Key}
                        type="text"
                        name="code"
                        placeholder={t('verification_code')}
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        maxLength={6}
                        required
                        error={fieldErrors.code}
                        inputClassName="auth-code"
                    />
                    {formError}
                    {submit(t('verify_button'))}
                    <div className="auth-links">
                        <button type="button" className="auth-link" onClick={() => go('LOGIN')}>{t('back_to_login')}</button>
                    </div>
                </form>
            )}

            {view === 'FORGOT_PASSWORD' && (
                <form onSubmit={handleSubmit} className="auth-form">
                    <p className="auth-desc">{t('forgot_password_desc')}</p>
                    {success}
                    <InputField icon={Mail} type="email" name="email" placeholder={t('email_placeholder')} value={formData.email} onChange={handleChange} error={fieldErrors.email} autoComplete="email" required />
                    {formError}
                    {submit(t('forgot_password_submit'), { disabled: !!successMsg })}
                    <div className="auth-links">
                        <button type="button" className="auth-link" onClick={() => go('LOGIN')}>{t('back_to_login')}</button>
                    </div>
                </form>
            )}
        </Modal>
    );
}
